import { Close as CloseIcon, OpenWith as OpenWithIcon } from '@mui/icons-material'
import { Box, Drawer, IconButton } from '@mui/material'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import BidHistoriesViewContent from '@/features/bidding/components/BidHistoriesViewContent'

interface BidOverviewDrawerContextProps {
  open: () => void
  close: () => void
  isOpen: boolean
}

const BidOverviewDrawerContext = createContext<BidOverviewDrawerContextProps | undefined>(undefined)

export const useBidOverviewDrawer = () => {
  const ctx = useContext(BidOverviewDrawerContext)
  if (!ctx) throw new Error('useBidOverviewDrawer must be used within a BidOverviewDrawerProvider')
  return ctx
}

export function BidOverviewDrawerContextProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [isOpen, setIsOpen] = useState(false)
  const [drawerWidth, setDrawerWidth] = useState<number>(600) // Default width in px
  const minDrawerWidth = 600
  const maxDrawerWidth = 1200
  const [isResizing, setIsResizing] = useState(false)
  const initialMouseX = useRef(0)
  const initialDrawerWidth = useRef(drawerWidth)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  // Mouse event handlers for resizing
  const handleMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      setIsResizing(true)
      initialMouseX.current = e.clientX
      initialDrawerWidth.current = drawerWidth
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [drawerWidth],
  )

  function handleMouseMove(e: MouseEvent) {
    if (!isResizing) return
    const deltaX = e.clientX - initialMouseX.current
    let newWidth = initialDrawerWidth.current + deltaX
    newWidth = Math.max(minDrawerWidth, Math.min(maxDrawerWidth, newWidth))
    setDrawerWidth(newWidth)
  }

  function handleMouseUp() {
    setIsResizing(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  useEffect(() => {
    if (isResizing) {
      globalThis.addEventListener('mousemove', handleMouseMove)
      globalThis.addEventListener('mouseup', handleMouseUp)
    } else {
      globalThis.removeEventListener('mousemove', handleMouseMove)
      globalThis.removeEventListener('mouseup', handleMouseUp)
    }
    return () => {
      globalThis.removeEventListener('mousemove', handleMouseMove)
      globalThis.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  return (
    <BidOverviewDrawerContext.Provider value={{ open, close, isOpen }}>
      <Drawer
        anchor="left"
        open={isOpen}
        sx={{
          '& .MuiDrawer-paper': {
            maxWidth: `${maxDrawerWidth}px`,
            minWidth: `${minDrawerWidth}px`,
            width: `${drawerWidth}px`,
          },
        }}
        variant="persistent"
        onClose={close}
      >
        <Box sx={{ pr: 2, pt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <IconButton sx={{ cursor: 'col-resize' }} onMouseDown={handleMouseDown}>
            <OpenWithIcon />
          </IconButton>
          <IconButton onClick={close}>
            <CloseIcon />
          </IconButton>
        </Box>
        <BidHistoriesViewContent shouldUpdateUrlWhenActiveTabChanges={false} />
      </Drawer>
      {children}
    </BidOverviewDrawerContext.Provider>
  )
}
